/**
 * ChatGPT: saved memories are NOT in the data export, so the paste lane is
 * primary. From an uploaded export zip we take `user.json` custom
 * instructions (preferences); a memories-shaped JSON is accepted defensively
 * in case the export ever grows one. Conversations are never read.
 */
import type { MemoryImportItem, NamedText } from '../types.js';
import { parseGenericJson, plainTextItems } from './generic.js';
import { basename } from './util.js';

export function parseChatgpt(files: NamedText[]): MemoryImportItem[] {
  const items: MemoryImportItem[] = [];
  for (const file of files) {
    const base = basename(file.name).toLowerCase();
    if (base === 'user.json') {
      items.push(...customInstructionItems(file.text));
    } else if (base === 'memories.json' || base === 'memory.json') {
      items.push(...parseGenericJson(file.name, file.text));
    }
  }
  return items;
}

function customInstructionItems(text: string): MemoryImportItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }
  if (typeof parsed !== 'object' || parsed === null) return [];
  const instructions = (parsed as Record<string, unknown>).custom_instructions;
  const parts: string[] =
    typeof instructions === 'string'
      ? [instructions]
      : typeof instructions === 'object' && instructions !== null
        ? Object.values(instructions).filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
        : [];
  return parts.flatMap((part) =>
    plainTextItems(part, { epistemic: 'preference', sourceDetail: 'Custom instructions' }),
  );
}
